import { defineStore } from 'pinia'
import { recommendedTopics } from '../utils/topicAssistance.mjs'

const DEFAULT_TOPIC = `You are required to propose a mini-research study on a prevailing trend/development/practice that is related to your study programme, discipline, or any topic of your interest. The study should aim to explore your target audience's attitudes and views on the topic.

You should include the following sections in your mini-research proposal:
1. Background: Explain the situation/phenomenon and introduce your mini-research topic, with related references
2. Objective: Describe the aim(s) of your research and explain its significance
3. Methodology: Describe the research period, target participants, and method(s)
4. References: Record the sources of information cited in the proposal in APA style (7th edition)

You should write about 150-200 words (excluding Section 4).

Below are some possible topics for your consideration:
- Workplace culture, e.g., work-life balance, office manners, and dress code
- Compensation and benefits packages, e.g., basic salary, commission, bonus, paid leave, housing, medical, and retirement
- Health and wellness programmes, e.g., stress management, yoga, and meditation
- Work policies, e.g., remote work and flexible schedules, work-from-home arrangements, and flexible leave arrangements
- Sustainable and eco-friendly practices, e.g., saving energy at work/home, waste reduction and recycling, and green transport/commuting
- Innovation and technology, e.g., artificial intelligence (AI), Internet of things (IoT), virtual reality (VR), and augmented reality (AR)
- Online platforms and mobile applications, e.g., social media platforms, e-commerce platforms, video streaming platforms, and banking apps
- Any trade-related trend/development/practice or topic of your interest`

function extractAssignmentPrompt(topic) {
  return topic.split('Below are some possible topics for your consideration:')[0].trim()
}

export const DRAFT_SECTIONS = [
  {
    key: 'background',
    label: 'Background',
    framework: [
      'Introducing the situation/phenomenon',
      'Citing evidence to explain the background',
      'Introducing the measure implemented in response to the situation',
    ],
    hintQuestions: [
      'What situation, trend, or phenomenon motivates your study?',
      'What gap, problem, or audience attitude makes this topic worth studying?',
      'What background source or evidence could support this context?',
    ],
  },
  {
    key: 'objective',
    label: 'Objective',
    framework: [
      'Defining the research aim(s)',
      'Explaining the significance of the study',
    ],
    hintQuestions: [
      'What is the main aim of your mini-research?',
      'Whose attitudes or views will your study explore?',
      'Why would the findings be useful or meaningful?',
    ],
  },
  {
    key: 'methodology',
    label: 'Methodology',
    framework: [
      'Timeframe',
      'Target participants',
      'Data collection method',
    ],
    hintQuestions: [
      'When will the research take place?',
      'Who are the target participants?',
      'What method will you use to collect data?',
    ],
  },
  {
    key: 'references',
    label: 'References',
    framework: [],
    hintQuestions: [],
  },
]

function emptyDraftSections() {
  return DRAFT_SECTIONS.reduce((acc, section) => {
    acc[section.key] = ''
    return acc
  }, {})
}

function initialMessages() {
  return []
}

function normaliseCefrLevel(level) {
  const value = String(level || '').trim().toUpperCase()
  return ['B1', 'B2', 'C1'].includes(value) ? value : 'B1'
}

function doiUrl(doi = '') {
  const cleaned = String(doi || '').trim()
  return cleaned ? `https://doi.org/${cleaned.replace(/^https?:\/\/doi.org\//, '')}` : ''
}

function normaliseReferenceText(value = '') {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

function normaliseReferenceUrl(value = '') {
  const raw = String(value || '').trim()
  if (!raw) return ''
  try {
    const url = new URL(raw)
    url.hash = ''
    url.search = ''
    return url.toString().replace(/\/$/, '').toLowerCase()
  } catch {
    return raw.replace(/\/$/, '').toLowerCase()
  }
}

function referenceIdentityKey(reference = {}) {
  const doi = normaliseReferenceText(reference.doi || '').replace(/^https?:\/\/doi.org\//, '')
  if (doi) return `doi:${doi}`
  const url = normaliseReferenceUrl(reference.paperUrl || reference.paper_url || reference.url || '')
  if (url) return `url:${url}`
  const title = normaliseReferenceText(reference.title || '')
  if (title) return `title:${title}:${reference.year || ''}`
  return ''
}

function mergeReference(existing, incoming) {
  for (const key of ['title', 'apa', 'originalText', 'paraphrase', 'url', 'doi', 'paperUrl']) {
    if (!existing[key] && incoming[key]) existing[key] = incoming[key]
  }
  if ((!existing.paraphraseOptions || !existing.paraphraseOptions.length) && incoming.paraphraseOptions?.length) {
    existing.paraphraseOptions = incoming.paraphraseOptions
  }
}

export const useEssayStore = defineStore('essay', {
  state: () => ({
    stage: 'topic',
    experimentParticipantId: null,
    topic: DEFAULT_TOPIC,
    researchTopic: '',
    assignmentId: null,
    configuredTopics: [],
    configuredTopicsSource: null,
    cefrLevel: 'B1',
    sectionGuidance: {},
    draftSections: emptyDraftSections(),
    activeDraftSection: 'background',
    references: [],
    messages: initialMessages(),
    scoreResult: null,
    teacherScore: null,
    teacherFeedback: '',
  }),
  getters: {
    activeSectionMeta: (state) => {
      const section = DRAFT_SECTIONS.find(item => item.key === state.activeDraftSection) || DRAFT_SECTIONS[0]
      const guidance = state.sectionGuidance[section.key] || {}
      return {
        ...section,
        framework: guidance.framework || section.framework || [],
        hintQuestions: guidance.questions || section.hintQuestions || [],
      }
    },
    combinedDraft: (state) => {
      const body = DRAFT_SECTIONS
        .filter(section => section.key !== 'references')
        .map(section => `${section.label}: ${state.draftSections[section.key] || ''}`.trim())
        .join('\n\n')
      const refs = state.references.length
        ? state.references.map(ref => ref.apa || ref.title || ref.url).join('\n')
        : state.draftSections.references
      return `${body}\n\nReferences:\n${refs || ''}`.trim()
    },
    assignmentPrompt: (state) => extractAssignmentPrompt(state.topic),
    topicRecommendations: (state) => ({...recommendedTopics(state.topic, state.configuredTopics), ...(state.configuredTopicsSource ? {source:state.configuredTopicsSource} : {})}),
    topicExamples: (state) => recommendedTopics(state.topic, state.configuredTopics).topics,
  },
  actions: {
    hydrateFromRecord(record) {
      if (!record) return
      this.experimentParticipantId = record.experiment?.participant_id || null
      this.assignmentId = record.assignment?.id || record.assignment_id || null
      this.topic = record.assignment?.prompt || DEFAULT_TOPIC
      this.configuredTopics = record.assignment?.suggested_topics || record.assignment?.topic_examples || record.task_configuration?.suggested_topics || []
      this.configuredTopicsSource = null
      this.researchTopic = record.research_topic || ''
      this.cefrLevel = normaliseCefrLevel(record.cefr_level)
      this.sectionGuidance = record.section_guidance || {}
      this.draftSections = { ...emptyDraftSections(), ...(record.draft_sections || {}) }
      this.references = []
      if (Array.isArray(record.references)) record.references.forEach(ref => this.addReference(ref))
      this.messages = Array.isArray(record.messages)
        ? record.messages.map(message => ({
          role: message.role,
          mode: message.mode,
          content: message.content,
          targetSentence: message.targetSentence || '',
          tool: message.tool || '',
        }))
        : []
      this.scoreResult = record.score_result && Object.keys(record.score_result).length ? record.score_result : null
      this.teacherScore = record.teacher_score ?? null
      this.teacherFeedback = record.teacher_feedback || ''
      this.stage = this.researchTopic || Object.values(this.draftSections).some(Boolean) ? 'drafting' : 'topic'
    },
    snapshotMessages() {
      return this.messages.map(message => ({
        role: message.role,
        mode: message.mode || '',
        content: message.content || '',
        targetSentence: message.targetSentence || '',
        tool: message.tool || '',
      }))
    },
    confirmTopic(topic, guidance) {
      this.researchTopic = topic || this.researchTopic || 'A mini-research topic selected through the chatbot discussion'
      this.sectionGuidance = guidance || {}
      this.stage = 'drafting'
      this.activeDraftSection = 'background'
    },
    setCefrLevel(level) {
      this.cefrLevel = normaliseCefrLevel(level)
    },
    setActiveDraftSection(key) {
      if (DRAFT_SECTIONS.some(section => section.key === key)) {
        this.activeDraftSection = key
      }
    },
    goDraftSection(delta) {
      const currentIndex = DRAFT_SECTIONS.findIndex(section => section.key === this.activeDraftSection)
      const nextIndex = (currentIndex + delta + DRAFT_SECTIONS.length) % DRAFT_SECTIONS.length
      this.activeDraftSection = DRAFT_SECTIONS[nextIndex].key
    },
    normaliseReference(reference = {}) {
      const id = reference.id || `ref-${Date.now()}-${Math.random().toString(16).slice(2)}`
      const doi = reference.doi || ''
      return {
        id,
        title: reference.title || '',
        apa: reference.apa || '',
        originalText: reference.originalText || reference.original_text || '',
        paraphrase: reference.paraphrase || reference.sourceSentence || reference.source_sentence || '',
        paraphraseOptions: reference.paraphraseOptions || reference.paraphrase_options || [],
        url: reference.url || '',
        doi,
        paperUrl: reference.paperUrl || reference.paper_url || reference.url || doiUrl(doi),
      }
    },
    findExistingReference(reference = {}) {
      const key = referenceIdentityKey(reference)
      if (!key) return null
      return this.references.find(item => referenceIdentityKey(item) === key) || null
    },
    addReference(reference = {}) {
      const normalised = this.normaliseReference(reference)
      const existing = this.findExistingReference(normalised)
      if (existing) {
        mergeReference(existing, normalised)
        return existing.id
      }
      this.references.push(normalised)
      return normalised.id
    },
    updateReference(id, patch) {
      const ref = this.references.find(item => item.id === id)
      if (ref) Object.assign(ref, patch)
    },
    deleteReference(id) {
      this.references = this.references.filter(item => item.id !== id)
    },
    resetConversation() {
      this.stage = 'topic'
      this.researchTopic = ''
      this.cefrLevel = 'B1'
      this.sectionGuidance = {}
      this.draftSections = emptyDraftSections()
      this.activeDraftSection = 'background'
      this.references = []
      this.messages = initialMessages()
      this.scoreResult = null
      this.teacherScore = null
      this.teacherFeedback = ''
    },
  },
})
